// Card de entidade do evento no detalhe do WL (§4.3) — inline
// expandable (Collapsible, padrão do repo p/ detalhe sem perder o
// contexto da lista): border-l-4 na cor do estado (ativo=emerald,
// inativo=neutro), ícone do tipo do evento, badges (tipo + N
// agendamentos), switch de enable com write OTIMISTA (AlertDialog só
// ao DESATIVAR) e, expandido, envio imediato + agendamentos + link
// p/ a Consulta filtrada em seções no padrão SectionCard.
import {
  BarChart3Icon,
  CalendarClockIcon,
  ChevronDownIcon,
  ClockIcon,
  MailIcon,
  PenIcon,
  PlusIcon,
  ZapIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"
import type {
  NotificationEventTemplateRefType,
  NotificationSearchChannelType,
  NotificationWhiteLabelChannelProviderType,
  NotificationWhiteLabelEventType,
} from "@/types/notification-center"

import {
  useToggleNotificationEvent,
  useUpdateEventImmediateTemplate,
} from "../configuration-mutations"
import { useNotificationCenterWhiteLabelsTemplates } from "../hooks"
import { dedupeTemplateOptionsByLatest } from "../template-options"
import { EventSchedulerCard } from "./event-scheduler-card"
import { SchedulerFormDialog } from "./scheduler-form-dialog"

const detailPrefix =
  "pages.notificationCenter.configurationsDetailPage"

// Envio imediato: um select de template POR CANAL (mesmo catálogo
// do WL usado no form de agendamento) para associar/trocar a
// associação — write otimista com rollback. Canais ativos do WL sem
// template aparecem com estado vazio + select para associar.
const ImmediateTemplatesEditor = ({
  whiteLabelId,
  event,
  activeChannels,
}: {
  whiteLabelId: string
  event: NotificationWhiteLabelEventType
  activeChannels: NotificationWhiteLabelChannelProviderType[]
}) => {
  const { t } = useTranslation()
  const updateImmediateTemplate =
    useUpdateEventImmediateTemplate(whiteLabelId)
  // Catálogo de templates do WL (só monta expandido → 1 fetch,
  // deduplicado entre eventos pela query key).
  const { data: rawTemplateOptions } =
    useNotificationCenterWhiteLabelsTemplates({
      whiteLabelId,
    })
  // Uma opção por template (versão ativa/maior): o GET traz uma linha
  // por versão e valores duplicados concatenam o texto no Select.
  const templateOptions = dedupeTemplateOptionsByLatest(
    rawTemplateOptions ?? []
  )

  // Troca de template já associado pede confirmação (write imediato);
  // associar num canal VAZIO é aditivo → aplica direto. (Hooks antes de
  // qualquer return condicional — rules-of-hooks.)
  const [pendingSwap, setPendingSwap] = useState<{
    channel: NotificationSearchChannelType
    templateUuid: string
    templateName: string
  } | null>(null)
  // Bump no cancelar força o remount do Select p/ restaurar o valor
  // atual (Radix mantém o item destacado ao cancelar sem escrever).
  const [cancelNonce, setCancelNonce] = useState(0)

  // Canais exibidos: os ATIVOS do WL + qualquer canal que já tenha
  // template no imediato (defensivo p/ canal desativado depois).
  const channels: NotificationSearchChannelType[] = [
    ...activeChannels.map(({ channel }) => channel),
    ...event.immediate.templates
      .map((template) => template.channel)
      .filter(
        (channel) =>
          !activeChannels.some(
            (active) => active.channel === channel
          )
      ),
  ]

  if (channels.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t(`${detailPrefix}.immediateEmpty`)}
      </p>
    )
  }

  const runSwap = (
    channel: NotificationSearchChannelType,
    templateUuid: string
  ) => {
    const option = templateOptions.find(
      (template) => template.id === templateUuid
    )
    updateImmediateTemplate.mutate({
      eventName: event.name,
      channel,
      templateUuid,
      // Ref resolvida (nome@versão) p/ o cache otimista exibir.
      optimisticTemplate: {
        channel,
        uuid: templateUuid,
        name: option?.name ?? templateUuid,
        version: option?.version ?? 1,
      },
    })
  }

  const handleChange = (
    channel: NotificationSearchChannelType,
    current: NotificationEventTemplateRefType | undefined,
    templateUuid: string
  ) => {
    if (templateUuid === current?.uuid) return
    if (current) {
      // Já tem template no canal → confirma a troca antes de escrever.
      const option = templateOptions.find(
        (template) => template.id === templateUuid
      )
      setPendingSwap({
        channel,
        templateUuid,
        templateName: option?.name ?? templateUuid,
      })
      return
    }
    runSwap(channel, templateUuid)
  }

  return (
    <>
      <ul className="flex flex-col gap-2">
        {channels.map((channel) => {
          const current = event.immediate.templates.find(
            (template) => template.channel === channel
          )
          // Garante a opção do template ATUAL mesmo se ele não estiver
          // no catálogo carregado (select nunca mostra vazio à toa).
          const options = [
            ...(current &&
            !templateOptions?.some(
              (template) => template.id === current.uuid
            )
              ? [
                  {
                    id: current.uuid,
                    name: current.name,
                    version: current.version,
                  },
                ]
              : []),
            ...(templateOptions ?? []),
          ]
          return (
            <li
              key={channel}
              className="flex flex-wrap items-center gap-2"
            >
              <Badge
                variant="outline"
                className="rounded-full font-medium"
              >
                {channel}
              </Badge>
              <Select
                // Radix mantém o último valor exibido quando o value
                // controlado volta a undefined (rollback) ou ao cancelar
                // a troca — o key força o remount e restaura o estado.
                key={`${current?.uuid ?? "no-template"}-${cancelNonce}`}
                value={current?.uuid}
                onValueChange={(templateUuid) =>
                  handleChange(
                    channel,
                    current,
                    templateUuid
                  )
                }
              >
                <SelectTrigger
                  className="h-8 w-72 max-w-full"
                  aria-label={t(
                    `${detailPrefix}.immediateTemplateFor`,
                    { channel }
                  )}
                >
                  <SelectValue
                    placeholder={t(
                      `${detailPrefix}.immediateSelectPlaceholder`
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {options.map((template) => (
                    <SelectItem
                      key={template.id}
                      value={template.id}
                    >
                      {template.name}
                      <span className="tabular-nums text-muted-foreground">
                        {" "}
                        @v{template.version}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {current ? (
                <Button variant="outline" size="sm" asChild>
                  <Link
                    to={`/notification-center/configurations/${whiteLabelId}/template-configuration/${current.uuid}`}
                    aria-label={t(
                      `${detailPrefix}.editTemplate`,
                      { name: current.name }
                    )}
                  >
                    <PenIcon
                      className="mr-1 size-4"
                      aria-hidden="true"
                    />
                    {t(
                      `${detailPrefix}.editTemplateAction`
                    )}
                  </Link>
                </Button>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {t(`${detailPrefix}.immediateNoTemplate`)}
                </span>
              )}
            </li>
          )
        })}
      </ul>
      {/* Confirmação ao TROCAR um template já associado (write imediato). */}
      <AlertDialog
        open={pendingSwap !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingSwap(null)
            setCancelNonce((nonce) => nonce + 1)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                `${detailPrefix}.immediateSwapDialog.title`
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                `${detailPrefix}.immediateSwapDialog.description`,
                {
                  channel: pendingSwap?.channel,
                  name: pendingSwap?.templateName,
                }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(
                `${detailPrefix}.immediateSwapDialog.cancel`
              )}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingSwap) {
                  runSwap(
                    pendingSwap.channel,
                    pendingSwap.templateUuid
                  )
                }
                setPendingSwap(null)
              }}
            >
              {t(
                `${detailPrefix}.immediateSwapDialog.confirm`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// Seção do conteúdo expandido no padrão SectionCard (header com
// ícone em barra bg-muted + corpo com padding).
const ExpandedSection = ({
  icon,
  title,
  action,
  children,
}: {
  icon: React.ReactNode
  title: React.ReactNode
  action?: React.ReactNode
  children: React.ReactNode
}) => (
  <div className="rounded-lg border bg-card">
    <div className="flex items-center justify-between gap-2 border-b bg-muted/50 px-4 py-2.5">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">
          {icon}
        </span>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
)

type WhiteLabelEventRowProps = {
  whiteLabelId: string
  event: NotificationWhiteLabelEventType
  activeChannels: NotificationWhiteLabelChannelProviderType[]
}

export const WhiteLabelEventRow = ({
  whiteLabelId,
  event,
  activeChannels,
}: WhiteLabelEventRowProps) => {
  const { t } = useTranslation()
  const [isDisableDialogOpen, setIsDisableDialogOpen] =
    useState(false)
  const [isAddSchedulerOpen, setIsAddSchedulerOpen] =
    useState(false)
  const toggleEvent =
    useToggleNotificationEvent(whiteLabelId)

  // Ativar é direto (otimista); desativar pede confirmação.
  const handleToggle = (checked: boolean) => {
    if (!checked) {
      setIsDisableDialogOpen(true)
      return
    }
    toggleEvent.mutate({
      eventName: event.name,
      enabled: true,
    })
  }

  const schedulersCount = event.schedulers.length
  const schedulersBadgeLabel =
    schedulersCount === 1
      ? t(`${detailPrefix}.schedulersBadgeOne`)
      : t(`${detailPrefix}.schedulersBadgeMany`, {
          count: schedulersCount,
        })

  const EventTypeIcon =
    event.type === "SCHEDULED" ? CalendarClockIcon : ZapIcon

  return (
    <li
      className={cn(
        "overflow-hidden rounded-xl border border-l-4 bg-card shadow-sm transition-shadow hover:shadow-md",
        event.enabled
          ? "border-l-emerald-500"
          : "border-l-zinc-300"
      )}
    >
      <Collapsible>
        <div className="flex items-center gap-3 p-4">
          {/* Ícone do evento na cor do estado. */}
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
              event.enabled
                ? "bg-emerald-50 text-emerald-600"
                : "bg-muted text-muted-foreground"
            )}
          >
            <EventTypeIcon
              className="size-5"
              aria-hidden="true"
            />
          </div>
          <CollapsibleTrigger
            className="group flex flex-1 items-center justify-between gap-2 text-left"
            aria-label={t(`${detailPrefix}.expandEvent`, {
              name: event.label,
            })}
          >
            <span className="flex min-w-0 flex-col gap-0.5">
              <span
                className={cn(
                  "text-sm font-semibold md:text-base",
                  !event.enabled && "text-muted-foreground"
                )}
              >
                {event.label}
              </span>
              <span className="truncate font-mono text-[11px] text-muted-foreground">
                {event.name}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <Badge
                variant="outline"
                className={cn(
                  "rounded-full border font-medium",
                  event.type === "SCHEDULED"
                    ? "border-violet-200 bg-violet-50 text-violet-700"
                    : "border-sky-200 bg-sky-50 text-sky-700"
                )}
              >
                {t(
                  `${detailPrefix}.typeBadge.${event.type}`
                )}
              </Badge>
              {schedulersCount > 0 && (
                <Badge
                  variant="outline"
                  className="rounded-full border-amber-200 bg-amber-50 font-medium tabular-nums text-amber-700"
                >
                  <ClockIcon
                    className="mr-1 size-3"
                    aria-hidden="true"
                  />
                  {schedulersBadgeLabel}
                </Badge>
              )}
              <ChevronDownIcon
                className="size-4 transition-transform group-data-[state=open]:rotate-180"
                aria-hidden="true"
              />
            </span>
          </CollapsibleTrigger>
          <Switch
            checked={event.enabled}
            onCheckedChange={handleToggle}
            aria-label={t(`${detailPrefix}.toggleEvent`, {
              name: event.label,
            })}
          />
        </div>

        <CollapsibleContent>
          <div className="space-y-4 border-t bg-muted/20 p-4">
            {/* Envio imediato: select de template por canal
                (associa/troca — write otimista). */}
            <ExpandedSection
              icon={
                <MailIcon size={16} aria-hidden="true" />
              }
              title={t(`${detailPrefix}.immediateTitle`)}
            >
              <ImmediateTemplatesEditor
                whiteLabelId={whiteLabelId}
                event={event}
                activeChannels={activeChannels}
              />
            </ExpandedSection>

            {/* Agendamentos (0..N) + adicionar. */}
            <ExpandedSection
              icon={
                <ClockIcon size={16} aria-hidden="true" />
              }
              title={t(`${detailPrefix}.schedulersTitle`)}
              action={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() =>
                    setIsAddSchedulerOpen(true)
                  }
                >
                  <PlusIcon
                    className="mr-1 size-4"
                    aria-hidden="true"
                  />
                  {t(`${detailPrefix}.addScheduler`)}
                </Button>
              }
            >
              {event.schedulers.length > 0 ? (
                <ul className="flex flex-col gap-3">
                  {event.schedulers.map((scheduler) => (
                    <EventSchedulerCard
                      key={scheduler.uuid}
                      whiteLabelId={whiteLabelId}
                      eventName={event.name}
                      eventLabel={event.label}
                      scheduler={scheduler}
                      activeChannels={activeChannels}
                    />
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t(`${detailPrefix}.schedulersEmpty`)}
                </p>
              )}
            </ExpandedSection>

            {/* Deep-link p/ a Consulta filtrada (§4.3). */}
            <Link
              to={`/notification-center/search?event=${event.name}&whiteLabelId=${whiteLabelId}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              <BarChart3Icon
                className="size-4"
                aria-hidden="true"
              />
              {t(`${detailPrefix}.viewNotifications`)}
            </Link>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Confirmação ao DESATIVAR (write otimista após confirmar). */}
      <AlertDialog
        open={isDisableDialogOpen}
        onOpenChange={setIsDisableDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${detailPrefix}.disableDialog.title`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                `${detailPrefix}.disableDialog.description`,
                { name: event.label }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${detailPrefix}.disableDialog.cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                toggleEvent.mutate({
                  eventName: event.name,
                  enabled: false,
                })
              }
            >
              {t(`${detailPrefix}.disableDialog.confirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Form de novo agendamento (renderizado só quando aberto). */}
      {isAddSchedulerOpen && (
        <SchedulerFormDialog
          whiteLabelId={whiteLabelId}
          eventName={event.name}
          activeChannels={activeChannels}
          onClose={() => setIsAddSchedulerOpen(false)}
        />
      )}
    </li>
  )
}
